<template>
  <div
    :class="{
      'card': true,
      'is-smallshadow': true,
      'complete': isFormComplete
    }">
    <div
      :class="{
        'card-body': true,
        'form-readonly': readonly
      }">
      <h2
        :class="{
          'heading-bigger mb-4': !readonly,
          'heading-small text-grey': readonly
        }">
        {{ $t('inquiryFinancialInfo.financialLiabilities') }}
      </h2>

      <template v-if="allDataLoaded">
        <div :class="{ 'width-narrow': !readonly }">
          <div class="form-group">
            <label class="wide" for="has-liabilities">{{ $t('inquiryLiabilitiesInfo.doYouHaveFinancialLiabilities') }}</label>

            <template v-if="!readonly">
              <validation-provider
                tag="div"
                rules="required"
                name="has-liabilities"
                v-slot="{ errors }">
                <f-form-radio
                  :value="true"
                  v-model="hasLiabilities">
                  {{ $t('ui.yes') }}
                </f-form-radio>
                <f-form-radio
                  :value="false"
                  v-model="hasLiabilities">
                  {{ $t('ui.no') }}
                </f-form-radio>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </template>
            <template v-else>
              <template v-if="hasLiabilities">
                {{ $t('ui.yes') }}
              </template>
              <template v-else>
                {{ $t('ui.no') }}
              </template>
            </template>
          </div>

          <f-button
            v-if="!readonly"
            :disabled="!hasLiabilities"
            theme="primary"
            @click.prevent="$bus.$emit('inquiry-liability-popup-show', null, allBanks, allLiabilities, allCurrencies)">
            {{ $t('inquiryLiabilitiesInfo.addCommitment') }}
          </f-button>
        </div>

        <div
          v-for="(liability, index) of data.liabilities"
          :key="'liability-' + index"
          :class="{
            'flex flex-align-items-center items-list': true,
            'mt-2 pb-2 full-width': true,
            'border-bottom': index < data.liabilities.length - 1
          }">
          <div class="item-index pr-1">
            <strong>{{ index + 1 }}.</strong>
          </div>
          <div class="item-data mr-2">
            <strong>{{ getLiabilityName(liability.liability_id)}}</strong>
          </div>
          <div class="item-data mr-2">
            <small>{{ getLiabilityLabel(liability.liability_id) }}: </small><br>
            <strong class="text-primary">{{ liability.instalment_amount }} {{ getLiabilityInstallmentCurrencyName(liability) }}</strong>
          </div>
          <div class="item-data">
            <template v-if="liability.liability_id !== 8">
              <small>{{ $t('inquiryFinancialInfo.forRepayment') }}: </small><br>
              <strong class="text-primary">{{ liability.payoff_amount }} {{ getLiabilityPayoffCurrencyName(liability) }}</strong>
            </template>
          </div>
          <div
            v-if="!readonly"
            class="item-options ml-2">
            <f-dropdown
              theme="minimal"
              type="more"
              :items="[
                {
                  type: 'button',
                  disabled: false,
                  event: 'inquiry-liability-edit',
                  param: liability,
                  label: $t('ui.edit'),
                  name: 'edit'
                },
                {
                  type: 'button',
                  disabled: false,
                  event: 'inquiry-liability-remove',
                  param: index,
                  label: $t('ui.remove'),
                  theme: 'danger',
                  name: 'delete'
                }
              ]"/>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="loading-spinner-wrapper">
          <div class="loading-spinner pb-3 pt-3">
            <span class="loading-spinner-content">
              {{ $t('ui.loadingDataInProgress') }}&hellip;
            </span>
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="!readonly"
      class="card-footer pt-3 pb-4 text-right">
      <f-button
        theme="primary"
        withArrow
        big
        @click.prevent="goToNextTab">
        {{ $t('ui.next') }}
      </f-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'inquiry-liabilities-info-form',
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    isDuplicate: {
      type: Boolean,
      default: false
    },
    liabilities: {
      type: Array,
      required: true
    },
    allLiabilities: {
      type: Array,
      default: () => []
    },
    allBanks: {
      type: Array,
      default: () => []
    },
    allCurrencies: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    isFormComplete() {
      let isComplete = false;
      if (this.hasLiabilities === false) {
        isComplete = true;
      } else if (this.hasLiabilities) {
        isComplete = this.data.liabilities.length > 0;
      }

      if (this.allDataLoaded && (isComplete !== this.previousIsCompleteSet)) {
        Vue.set(this, 'previousIsCompleteSet', isComplete);
        this.$bus.$emit('inquiry-form-part-complete', this.index, isComplete, 'liabilities-info');
      }

      return isComplete;
    },
    allDataLoaded () {
      return this.loaded;
    }
  },
  data() {
    return {
      data: {
        liabilities: [],
      },
      hasLiabilities: null,
      loaded: false,
      loadError: false,
      previousIsCompleteSet: false
    };
  },
  mounted () {
    this.$bus.$on('inquiry-liability-edit', this.editLiability);
    this.$bus.$on('inquiry-liability-remove', this.removeLiability);
    this.$bus.$on('inquiry-liability-popup-submit', this.saveLiability);

    Vue.nextTick(() => {
      this.data.liabilities = this.liabilities;

      Vue.set(this.data, 'liabilities', JSON.parse(JSON.stringify(this.liabilities)));

      if (this.isEdit || this.isDuplicate) {
        this.hasLiabilities = this.liabilities.length > 0;
      }
    });

    this.loaded = true;
  },
  methods: {
    goToNextTab () {
      this.$bus.$emit('inquiry-form-go-next-tab', this.index + 1);
    },
    getLiabilityName (liabilityID) {
      let liabilityItem = this.allLiabilities.find(item => item.id === liabilityID);
      if (!liabilityID || !liabilityItem) {
        return this.$t('ui.none');
      }

      return liabilityItem.name;
    },
    getLiabilityLabel (liabilityID) {
      let liablity = this.allLiabilities.find(item => item.id === liabilityID);

      if (!liabilityID || !liablity || !liablity.label) {
        return 'Wysokość raty';
      }

      return liablity.label;
    },
    editLiability (liability) {
      this.$bus.$emit('inquiry-liability-popup-show', liability, this.allBanks, this.allLiabilities, this.allCurrencies);
    },
    removeLiability (liabilityIndex) {
      Vue.swal({
        title: this.$t('inquiryFinancialInfo.areYouSureYouWantToRemoveCommitment'),
        text: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yesRemove'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.data.liabilities.splice(liabilityIndex, 1);
          Vue.swal({
            title: this.$t('inquiryFinancialInfo.commitmentHasBeenRemove'),
            html: this.$t('inquiryFinancialInfo.offerMustBeRecorded'),
            icon: 'success',
            confirmButtonText: 'OK',
            buttonsStyling: true
          })
        }
      });
    },
    getData () {
      let liabilitiesOutput = [];
      for (let i = 0; i < this.data.liabilities.length; i++) {
        liabilitiesOutput.push(JSON.parse(JSON.stringify(this.data.liabilities[i])));
      }

      return {
        liabilities: liabilitiesOutput
      };
    },
    saveLiability (liabilityData, isLiabilityEdit) {
      if (!isLiabilityEdit) {
        this.data.liabilities.push(liabilityData);
      }
    },
    getLiabilityInstallmentCurrencyName (liablity) {
      if (!this.allCurrencies || !this.allCurrencies.length) {
        return '';
      }
      if (liablity.instalment_amount_currency_id) {
        let currency = this.allCurrencies.find(item => item.id === liablity.instalment_amount_currency_id);
        if (currency.code === 'OTHER') {
          return liablity.instalment_amount_currency_name;
        }

        return currency.code;
      }

      let defaultCurrency = this.allCurrencies.find(item => item.code === 'PLN');

      if (defaultCurrency) {
        return defaultCurrency.code;
      }

      return this.allCurrencies[0].code;
    },
    getLiabilityPayoffCurrencyName (liablity) {
      if (!this.allCurrencies || !this.allCurrencies.length) {
        return '';
      }
      if (liablity.payoff_amount_currency_id) {
        let currency = this.allCurrencies.find(item => item.id === liablity.payoff_amount_currency_id);
        if (currency.code === 'OTHER') {
          return liablity.payoff_amount_currency_name;
        }

        return currency.code;
      }

      let defaultCurrency = this.allCurrencies.find(item => item.code === 'PLN');

      if (defaultCurrency) {
        return defaultCurrency.code;
      }

      return this.allCurrencies[0].code;
    }
  },
  beforeDestroy () {
    this.$bus.$off('inquiry-liability-edit', this.editLiability);
    this.$bus.$off('inquiry-liability-remove', this.removeLiability);
    this.$bus.$off('inquiry-liability-popup-submit', this.saveLiability);
  }
}
</script>

